import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

type PreviewChartType = 'bar' | 'pie' | 'donut' | 'line';

interface SubscriptionLockProps {
    message?: string;
    ctaLabel?: string;
    onCtaClick?: () => void;
    previewChartType?: PreviewChartType;
}



const BLUR_CHART_DATA = [
    { name: 'Jan', value: 30 },
    { name: 'Feb', value: 20 },
    { name: 'Mar', value: 50 },
    { name: 'Apr', value: 40 },
    { name: 'May', value: 60 },
    { name: 'Jun', value: 35 },
];



// 8 unique, visually distinct palettes (4 material-inspired, 4 highcharts/other inspired)
const COLOR_PALETTES = [
  // Material-inspired
  ['#90caf9', '#f48fb1', '#a5d6a7', '#ffe082', '#ce93d8', '#ffab91'],
  ['#42a5f5', '#ef5350', '#66bb6a', '#ffd54f', '#ab47bc', '#ff7043'],
  ['#1976d2', '#c62828', '#2e7d32', '#ffb300', '#6a1b9a', '#d84315'],
  ['#64b5f6', '#e57373', '#81c784', '#fff176', '#ba68c8', '#ff8a65'],
  // Highcharts/other unique
  ['#7cb5ec', '#434348', '#90ed7d', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f', '#f45b5b', '#91e8e1'],
  ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'],
  ['#003f5c', '#58508d', '#bc5090', '#ff6361', '#ffa600', '#00876c', '#89c079', '#ffd166', '#ef476f', '#118ab2'],
  ['#22223b', '#4a4e69', '#9a8c98', '#c9ada7', '#f2e9e4', '#22223b', '#f72585', '#b5179e', '#7209b7', '#3a0ca3'],
];


function getRandomPalette() {
  return COLOR_PALETTES[Math.floor(Math.random() * COLOR_PALETTES.length)];
}

function getRandomChartType(): PreviewChartType {
    const types: PreviewChartType[] = ['bar', 'pie', 'donut', 'line'];
    return types[Math.floor(Math.random() * types.length)];
}



const getPreviewChartOptions = (
  data: { name: string; value: number }[],
  chartType: PreviewChartType,
  palette: string[]
) => {
  if (chartType === 'pie' || chartType === 'donut') {
    return {
      chart: {
        type: 'pie',
        backgroundColor: 'rgba(0,0,0,0)',
        height: null,
        width: null,
      },
      title: { text: '' },
      credits: { enabled: false },
      tooltip: { enabled: false },
      plotOptions: {
        pie: {
          innerSize: chartType === 'donut' ? '60%' : '0%',
          dataLabels: { enabled: false },
          borderWidth: 0,
        },
        series: {
          enableMouseTracking: false,
          animation: false,
        },
      },
      legend: { enabled: false },
      series: [
        {
          type: 'pie',
          data: data.map((item, i) => ({ name: item.name, y: item.value, color: palette[i % palette.length] })),
        },
      ],
    };
  }
  
  if (chartType === 'line') {
    // Line chart: randomize values for visual variety
    const lineData = data.map((item) => {
      const factor = 0.7 + Math.random() * 0.6; // 0.7 to 1.3
      return { ...item, value: Math.round(item.value * factor) };
    });
    
    // Use bright light blue color scheme that shows through grayscale filter
    const lineColor = '#42a5f5'; // Brighter light blue
    const fillColor = {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [
        [0, 'rgba(66, 165, 245, 0.4)'], // Brighter light blue with transparency
        [1, 'rgba(66, 165, 245, 0.1)'], // Very light blue at bottom
      ],
    };
    
    return {
      chart: {
        type: 'line',
        backgroundColor: 'rgba(0,0,0,0)',
        height: null,
        width: null,
      },
      title: { text: '' },
      credits: { enabled: false },
      xAxis: {
        categories: lineData.map((item) => item.name),
        labels: { enabled: false },
        lineWidth: 0,
        tickLength: 0,
      },
      yAxis: {
        visible: false,
      },
      legend: { enabled: false },
      plotOptions: {
        line: {
          color: lineColor,
          lineWidth: 3,
          marker: {
            enabled: false,
          },
          fillColor: fillColor,
        },
        series: {
          enableMouseTracking: false,
          states: {
            inactive: { enabled: false },
            hover: { enabled: false },
          },
          animation: false,
        },
        area: {
          fillOpacity: 0.3,
        },
      },
      series: [
        {
          type: 'line',
          fillOpacity: 0.3,
          data: lineData.map((item) => item.value),
          color: lineColor,
          fillColor: fillColor,
        },
      ],
      tooltip: { enabled: false },
    };
  }
  
  // bar chart: randomize bar heights for visual variety
  let barData = data;
  if (chartType === 'bar') {
    // Add a random factor to each value (±10-30%) for visual difference
    barData = data.map((item) => {
      const factor = 0.7 + Math.random() * 0.6; // 0.7 to 1.3
      return { ...item, value: Math.round(item.value * factor) };
    });
  }
  return {
    chart: {
      type: 'column',
      backgroundColor: 'rgba(0,0,0,0)',
      height: null,
      width: null,
    },
    title: { text: '' },
    credits: { enabled: false },
    xAxis: {
      categories: barData.map((item) => item.name),
      labels: { enabled: false },
      lineWidth: 0,
      tickLength: 0,
    },
    yAxis: {
      visible: false,
    },
    legend: { enabled: false },
    plotOptions: {
      column: {
        borderRadius: 4,
        pointPadding: 0.2,
        groupPadding: 0.1,
      },
      series: {
        enableMouseTracking: false,
        states: {
          inactive: { enabled: false },
          hover: { enabled: false },
        },
        animation: false,
        colorByPoint: true,
      },
    },
    series: [
      {
        type: 'column',
        data: barData.map((item, i) => ({ y: item.value, color: palette[i % palette.length] })),
      },
    ],
    tooltip: { enabled: false },
  };
};



export const SubscriptionLock: React.FC<SubscriptionLockProps> = ({
    message = 'Register your project with us to unlock more analysis',
    ctaLabel = 'Register',
    onCtaClick,
    previewChartType,
}) => {
    // Memoize random chart type and palette for stable rendering
    const chartType = React.useMemo(
        () => previewChartType || getRandomChartType(),
        [previewChartType]
    );
    const palette = React.useMemo(() => getRandomPalette(), []);

    const chartOptions = React.useMemo(() => {
        return getPreviewChartOptions(BLUR_CHART_DATA, chartType, palette);
    }, [chartType, palette]);

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                minHeight: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                borderRadius: 2,
            }}
        >
            {/* Blurred Chart Background */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    minHeight: 400,
                    filter: chartType === 'line' 
                        ? 'blur(6px) grayscale(0.1)' // Less grayscale for line charts to preserve color
                        : 'blur(6px) grayscale(0.3)',
                    opacity: 0.7,
                    zIndex: 1,
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ width: '100%', height: '100%', minHeight: 400 }}>
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={chartOptions}
                        containerProps={{ style: { width: '100%', height: '100%', minHeight: 400 } }}
                    />
                </Box>
            </Box>
            {/* Centered Lock and Message */}
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.85)',
                    borderRadius: 2,
                    p: { xs: 2, sm: 3 },
                    boxShadow: 1,
                    width: '100%',
                    maxWidth: 400,
                    m: 'auto',
                }}
            >
                <LockOutlinedIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} data-testid="LockOutlinedIcon" />
                <Typography variant="h6" align="center" sx={{ mb: 2 }}>
                    {message}
                </Typography>
                <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={onCtaClick}
                >
                    {ctaLabel}
                </Button>
            </Box>
        </Box>
    );
};

export default SubscriptionLock;
